import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Plus, Pencil, Trash2, X } from "lucide-react";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function toMonthInput(isoDate) {
  // Backend stores YYYY-MM-DD. Return YYYY-MM for the <input type="month"/>.
  if (!isoDate) return "";
  return isoDate.slice(0, 7);
}

function fromMonthInput(value, isEnd) {
  // Convert YYYY-MM to YYYY-MM-DD. Start month -> day 01, end month -> last day.
  if (!value) return "";
  const [y, m] = value.split("-").map(Number);
  if (!y || !m) return "";
  if (!isEnd) return `${y}-${String(m).padStart(2, "0")}-01`;
  const last = new Date(y, m, 0).getDate();
  return `${y}-${String(m).padStart(2, "0")}-${String(last).padStart(2, "0")}`;
}

function formatMonth(isoDate) {
  if (!isoDate) return "—";
  const [y, m] = isoDate.split("-").map(Number);
  if (!y || !m) return isoDate;
  return `${MONTHS[m - 1]} ${y}`;
}

function defaultSeasonName(startIso, endIso) {
  if (!startIso || !endIso) return "";
  const sy = startIso.slice(0, 4);
  const ey = endIso.slice(0, 4);
  return sy === ey ? sy : `${sy}–${ey}`;
}

function MonthYearPicker({ value, onChange, isEnd, testid }) {
  // value is YYYY-MM-DD or "".
  const [y, m] = value ? value.split("-").map(Number) : [null, null];
  const thisYear = new Date().getFullYear();
  const years = [];
  for (let yr = thisYear - 5; yr <= thisYear + 10; yr++) years.push(yr);

  const update = (newY, newM) => {
    if (!newY || !newM) {
      onChange("");
      return;
    }
    if (isEnd) {
      const last = new Date(newY, newM, 0).getDate();
      onChange(`${newY}-${String(newM).padStart(2, "0")}-${String(last).padStart(2, "0")}`);
    } else {
      onChange(`${newY}-${String(newM).padStart(2, "0")}-01`);
    }
  };

  return (
    <div className="grid grid-cols-2 gap-2">
      <select
        className="ccs-input"
        value={m || ""}
        onChange={(e) => update(y || thisYear, Number(e.target.value))}
        required
        data-testid={`${testid}-month`}
      >
        <option value="">Month</option>
        {MONTHS.map((name, i) => (
          <option key={i + 1} value={i + 1}>{name}</option>
        ))}
      </select>
      <select
        className="ccs-input"
        value={y || ""}
        onChange={(e) => update(Number(e.target.value), m || 1)}
        required
        data-testid={`${testid}-year`}
      >
        <option value="">Year</option>
        {years.map((yr) => (
          <option key={yr} value={yr}>{yr}</option>
        ))}
      </select>
    </div>
  );
}

export default function Seasons() {
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN";
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => setList((await api.get("/seasons")).data);
  useEffect(() => { load(); }, []);

  const remove = async (s) => {
    if (!window.confirm(`Delete season "${s.season_name}"?`)) return;
    try {
      await api.delete(`/seasons/${s.id}`);
      load();
    } catch (ex) {
      alert(formatApiError(ex.response?.data?.detail));
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-5xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="ccs-label mb-2">Calendar</div>
          <h1 className="text-4xl font-semibold tracking-tight">Seasons</h1>
        </div>
        {isAdmin && (
          <button
            onClick={() =>
              setEditing({ season_name: "", start_date: "", end_date: "", is_active: false })
            }
            className="ccs-btn"
            data-testid="add-season-button"
          >
            <Plus size={16} /> New season
          </button>
        )}
      </div>

      {isAdmin && (
        <div className="muted-text text-xs mb-6">
          The public share link has moved — manage it under <span className="mono">Settings → Public results link</span>.
        </div>
      )}

      <div className="ccs-card p-0 overflow-x-auto">
        <table className="ccs-table">
          <thead>
            <tr>
              <th>Season</th>
              <th>Start</th>
              <th>End</th>
              <th>Status</th>
              <th style={{ width: 120 }}></th>
            </tr>
          </thead>
          <tbody>
            {list.map((s) => (
              <tr key={s.id}>
                <td className="font-medium">{s.season_name}</td>
                <td className="mono muted-text">{formatMonth(s.start_date)}</td>
                <td className="mono muted-text">{formatMonth(s.end_date)}</td>
                <td>
                  {s.is_active ? <span className="active-pill">Active</span> : <span className="tag">Archived</span>}
                </td>
                <td className="text-right">
                  {isAdmin && (
                    <>
                      <button onClick={() => setEditing(s)} className="p-2 hover:bg-neutral-800">
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => remove(s)} className="p-2 hover:bg-neutral-800 text-red-400">
                        <Trash2 size={14} />
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr><td colSpan={5} className="text-center py-10 muted-text">No seasons yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <SeasonDialog
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function SeasonDialog({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const isEdit = !!initial.id;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const payload = {
        season_name: form.season_name,
        start_date: form.start_date,
        end_date: form.end_date,
        is_active: !!form.is_active,
      };
      if (isEdit) await api.patch(`/seasons/${initial.id}`, payload);
      else await api.post("/seasons", payload);
      onSaved();
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="dialog-bg" onClick={onClose}>
      <form className="dialog-panel p-6" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-xl font-semibold">{isEdit ? "Edit" : "New"} season</h3>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="form-row">
          <label className="ccs-label">Season name</label>
          <input
            required
            className="ccs-input"
            value={form.season_name}
            onChange={(e) => setForm({ ...form, season_name: e.target.value })}
            placeholder={defaultSeasonName(form.start_date, form.end_date) || "2025–2026"}
            data-testid="season-name-input"
          />
          {!form.season_name && defaultSeasonName(form.start_date, form.end_date) && (
            <button
              type="button"
              className="text-amber-400 text-xs text-left mt-1"
              onClick={() =>
                setForm({ ...form, season_name: defaultSeasonName(form.start_date, form.end_date) })
              }
            >
              Use “{defaultSeasonName(form.start_date, form.end_date)}”
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="form-row">
            <label className="ccs-label">Start month</label>
            <MonthYearPicker
              value={form.start_date}
              onChange={(v) => setForm({ ...form, start_date: v })}
              isEnd={false}
              testid="season-start"
            />
          </div>
          <div className="form-row">
            <label className="ccs-label">End month</label>
            <MonthYearPicker
              value={form.end_date}
              onChange={(v) => setForm({ ...form, end_date: v })}
              isEnd={true}
              testid="season-end"
            />
          </div>
        </div>
        <label className="flex items-center gap-2 mt-2 text-sm cursor-pointer">
          <input
            type="checkbox" checked={!!form.is_active}
            onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
          />
          Set as active season
        </label>
        {err && <div className="danger-text text-sm mt-3">{err}</div>}
        <div className="flex justify-end gap-3 mt-6">
          <button type="button" onClick={onClose} className="ccs-btn-ghost">Cancel</button>
          <button type="submit" disabled={busy} className="ccs-btn" data-testid="save-season">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}
